import { useRef, useState } from 'react'
import emailjs from '@emailjs/browser'
import { motion, useInView } from 'framer-motion'
import { Mail, Phone, MapPin, Github, Linkedin, Twitter, Send, CheckCircle } from 'lucide-react'
import AnimatedBackground from './AnimatedBackground'


export default function Contact() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID as string
      const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID as string
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY as string

      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.name,
          reply_to: formData.email,
          subject: formData.subject,
          message: formData.message
        },
        { publicKey }
      )

      setIsSubmitted(true)
      setFormData({ name: '', email: '', subject: '', message: '' })
      setTimeout(() => setIsSubmitted(false), 5000)
    } catch (err) {
      console.error('Email send failed', err)
      alert('Failed to send message. Please try again later.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'prithviganiger@gmail.com',
      href: 'mailto:prithviganiger@gmail.com',
      color: '#00ff41'
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+91 7338538735',
      href: 'tel:+917338538735',
      color: '#00ffff'
    },
    {
      icon: MapPin,
      label: 'Location',
      value: 'Hubli, Karnataka, India',
      href: '#',
      color: '#ff0080'
    }
  ]

  const socialLinks = [
    {
      icon: Github,
      href: 'https://github.com/16Prithvi',
      label: 'GitHub',
      color: '#8b00ff'
    },
    {
      icon: Linkedin,
      href: 'https://www.linkedin.com/in/prithvi-ganiger-10bb11365',
      label: 'LinkedIn',
      color: '#00ffff'
    },
    {
      icon: Twitter,
      href: '#',
      label: 'Twitter',
      color: '#ff0080'
    }
  ]

  return (
    <section id="contact" ref={ref} className="relative py-12 md:py-16 min-h-0 bg-black overflow-hidden">
      {/* Animated Background */}
      <AnimatedBackground variant="gradient" intensity="medium" />
      <AnimatedBackground variant="waves" intensity="low" />
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-display font-black mb-4">
            <span className="glitch-text text-neon-purple neon-glow" data-text="CONTACT">
              CONTACT
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-neon-purple to-neon-yellow mx-auto mb-8" />
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Let's connect and build something amazing together
          </p>
        </motion.div>


        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="bg-dark-800/50 backdrop-blur-md rounded-xl p-8 border border-neon-green/20 neon-border">
              <h3 className="text-2xl font-display font-bold text-neon-green mb-6">
                GET IN TOUCH
              </h3>
              
              <div className="space-y-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon
                  return (
                    <motion.a
                      key={index}
                      href={info.href}
                      initial={{ opacity: 0, x: -20 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                      className="flex items-center space-x-4 p-4 rounded-lg hover:bg-neon-green/10 transition-all duration-300 group"
                    >
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                        style={{ backgroundColor: `${info.color}20` }}
                      >
                        <Icon size={24} style={{ color: info.color }} />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm font-mono">{info.label}</p>
                        <p className="text-white font-mono group-hover:text-neon-green transition-colors">
                          {info.value}
                        </p>
                      </div>
                    </motion.a>
                  )
                })}
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-dark-800/50 backdrop-blur-md rounded-xl p-8 border border-neon-blue/20 neon-border">
              <h3 className="text-2xl font-display font-bold text-neon-blue mb-6">
                SOCIAL PROFILES
              </h3>
              
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon
                  return (
                    <motion.a
                      key={index}
                      href={social.href}
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={isInView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="w-12 h-12 rounded-lg flex items-center justify-center border-2 hover:bg-neon-green/10 transition-all duration-300 group"
                      style={{ 
                        borderColor: social.color,
                        color: social.color
                      }}
                    >
                      <Icon size={20} className="group-hover:scale-110 transition-transform duration-300" />
                    </motion.a>
                  )
                })}
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.4 }}
            className="bg-dark-800/50 backdrop-blur-md rounded-xl p-8 border border-neon-pink/20 neon-border"
          >
            <h3 className="text-2xl font-display font-bold text-neon-pink mb-6">
              SEND MESSAGE
            </h3>

            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <CheckCircle className="text-neon-green mx-auto mb-4" size={64} />
                <h4 className="text-2xl font-display font-bold text-neon-green mb-2">
                  MESSAGE SENT!
                </h4>
                <p className="text-gray-300 font-mono">
                  Thank you for reaching out. I'll get back to you soon!
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 font-mono text-sm mb-2">
                      NAME
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-dark-700 border border-neon-green/30 rounded-lg px-4 py-3 text-white font-mono focus:border-neon-green focus:outline-none transition-colors duration-300"
                      placeholder="Your Name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 font-mono text-sm mb-2">
                      EMAIL
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-dark-700 border border-neon-green/30 rounded-lg px-4 py-3 text-white font-mono focus:border-neon-green focus:outline-none transition-colors duration-300"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 font-mono text-sm mb-2">
                    SUBJECT
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full bg-dark-700 border border-neon-green/30 rounded-lg px-4 py-3 text-white font-mono focus:border-neon-green focus:outline-none transition-colors duration-300"
                    placeholder="Subject"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 font-mono text-sm mb-2">
                    MESSAGE
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-dark-700 border border-neon-green/30 rounded-lg px-4 py-3 text-white font-mono focus:border-neon-green focus:outline-none transition-colors duration-300 resize-none"
                    placeholder="Your message here..."
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full bg-gradient-to-r from-neon-green to-neon-blue text-dark-900 font-mono font-bold text-lg uppercase tracking-wider py-4 rounded-lg hover:from-neon-blue hover:to-neon-pink transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed neon-border"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-dark-900 border-t-transparent rounded-full animate-spin" />
                      <span>SENDING...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Send size={20} />
                      <span>SEND MESSAGE</span>
                    </div>
                  )}
                </motion.button>
              </form>
            )}
          </motion.div>
        </div>

        {/* Footer Message */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.8 }}
          className="text-center mt-12"
        >
          <p className="text-lg md:text-xl font-mono text-gray-300 mb-3">
            Ready to bring your ideas to life?
          </p>
          <p className="text-base md:text-lg text-neon-green font-mono">
            Let's create something extraordinary together!
          </p>
        </motion.div>
      </div>
    </section>
  )
}